import React, { createContext, useState, useMemo, useContext, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import Layout from './components/Layout/Layout';
import AdminLayout from './components/Layout/AdminLayout';
import Home from './pages/Home';
import Article from './pages/Article';
import Search from './pages/Search';
import Favorites from './pages/Favorites';
import CategoryPage from './pages/Category';
import Auth from './pages/admin/Auth';
import AdminRedirect from './pages/admin/AdminRedirect';
import Categories from './pages/admin/Categories';
import Subcategories from './pages/admin/Subcategories';
import Articles from './pages/admin/Articles';
import UsersList from './pages/admin/UsersList';
import UserForm from './pages/admin/UserForm';
import ArticleForm from './pages/admin/ArticleForm';
import CategorySettings from './pages/admin/CategorySettings';
import ArticleView from './pages/admin/ArticleView';
import Media from './pages/admin/Media';


// Создаем контекст для темы
interface ThemeContextType {
  mode: 'light' | 'dark';
  toggleTheme: () => void;
}

export const ThemeContext = createContext<ThemeContextType>({
  mode: 'light',
  toggleTheme: () => {},
});

// Хук для использования темы
export const useThemeMode = () => useContext(ThemeContext);


function App() {
  // Проверяем сохраненную тему в localStorage или используем системные настройки
  const getInitialMode = (): 'light' | 'dark' => {
    const savedMode = localStorage.getItem('themeMode');
    if (savedMode && (savedMode === 'light' || savedMode === 'dark')) {
      return savedMode;
    }
    // Проверяем системные настройки
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  };

  const [mode, setMode] = useState<'light' | 'dark'>(getInitialMode);

  // Сохраняем изменения темы в localStorage
  useEffect(() => {
    localStorage.setItem('themeMode', mode);
  }, [mode]);

  // Функция для переключения темы
  const toggleTheme = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  // Создаем контекст для темы
  const themeContext = useMemo(
    () => ({
      mode,
      toggleTheme,
    }),
    [mode]
  );

  // Создаем тему MUI
  const theme = useMemo(
    () =>
      createTheme({
  palette: {
          mode,
    primary: {
      main: '#2196f3',
      light: '#64b5f6',
      dark: '#1976d2',
    },
    secondary: {
      main: '#4caf50',
      light: '#81c784',
      dark: '#388e3c',
    },
    error: {
      main: '#f44336',
      light: '#e57373',
      dark: '#d32f2f',
    },
    warning: {
      main: '#ffa726',
      light: '#ffb74d',
      dark: '#f57c00',
    },
          background: {
            default: mode === 'light' ? '#f5f7fa' : '#121212',
            paper: mode === 'light' ? '#ffffff' : '#1e1e1e',
          },
  },
  typography: {
    fontFamily: '"Roboto", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
    },
    h2: {
      fontWeight: 600,
    },
    h3: {
      fontWeight: 600,
    },
  },
        shape: {
          borderRadius: 12,
        },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
                boxShadow: mode === 'light' 
                  ? '0 6px 12px rgba(0,0,0,0.05)'
                  : '0 6px 12px rgba(0,0,0,0.2)',
              },
            },
          },
          MuiButton: {
            styleOverrides: {
              root: {
                borderRadius: 8,
                textTransform: 'none',
                fontWeight: 500,
              },
            },
          },
          MuiListItemButton: {
            styleOverrides: {
              root: {
                borderRadius: 8,
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundImage: 'none',
        },
      },
    },
  },
      }),
    [mode]
  );

  return (
    <ThemeContext.Provider value={themeContext}>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Routes>
          {/* Админ маршруты */}
          <Route path="/admin" element={<AdminRedirect />} />
          <Route path="/admin/auth" element={<Auth />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route path="panel" element={<Articles />} />
            <Route path="panel/categories" element={<Categories />} />
            <Route path="panel/subcategories" element={<Subcategories />} />
            <Route path="panel/category-settings" element={<CategorySettings />} />
            <Route path="panel/articles" element={<Articles />} />
            <Route path="panel/articles/create" element={<ArticleForm />} />
            <Route path="panel/articles/edit/:articleId" element={<ArticleForm />} />
            <Route path="panel/articles/view/:articleId" element={<ArticleView />} />
            <Route path="panel/users/list" element={<UsersList />} />
            <Route path="panel/users/create" element={<UserForm />} />
            <Route path="panel/users/edit/:userId" element={<UserForm />} />
            <Route path="panel/media" element={<Media />} />
          </Route>

          {/* Основные маршруты */}
          <Route element={<Layout />}>
            <Route path="/" element={<Home />} />
            <Route path="/article/:categoryId/:subcategoryId/:id" element={<Article />} />
            <Route path="/category/:categoryId" element={<CategoryPage />} />
            <Route path="/category/:categoryId/:subcategoryId" element={<CategoryPage />} />
            <Route path="/search" element={<Search />} />
            <Route path="/favorites" element={<Favorites />} />
          </Route>
        </Routes>
      </Router>
    </ThemeProvider>
    </ThemeContext.Provider>
  );
}

export default App;
